import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { cva } from "class-variance-authority";
import { cn } from "../lib/cn";

const MotionLink = motion(Link);
const MotionA = motion.a;
const MotionButton = motion.button;

const base =
  "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition will-change-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent focus-visible:ring-offset-2 focus-visible:ring-offset-fk-surface dark:focus-visible:ring-offset-gray-950 disabled:pointer-events-none disabled:opacity-60";

const buttonVariants = cva(base, {
  variants: {
    variant: {
      primary:
        "bg-fk-ink text-white shadow-[0_10px_30px_rgba(11,18,32,0.18)] hover:shadow-[0_14px_40px_rgba(11,18,32,0.22)] hover:bg-fk-ink/95 active:bg-fk-ink dark:bg-white dark:text-gray-950 dark:hover:bg-white/95",
      secondary:
        "bg-fk-surface/70 text-fk-ink ring-1 ring-fk-border shadow-sm hover:bg-fk-surface active:bg-fk-surface dark:bg-white/5 dark:text-gray-100 dark:ring-white/10 dark:hover:bg-white/10 dark:active:bg-white/10",
      ghost:
        "bg-transparent text-fk-ink ring-1 ring-fk-border hover:bg-fk-ink/[0.03] active:bg-fk-ink/[0.06] dark:text-gray-100 dark:ring-white/10 dark:hover:bg-white/5 dark:active:bg-white/10",
      accent:
        "bg-fk-accent2 text-white shadow-[0_10px_30px_rgba(96,165,250,0.28)] hover:shadow-[0_14px_40px_rgba(96,165,250,0.34)] hover:bg-fk-accent2/90 active:bg-fk-accent2",
    },
  },
  defaultVariants: { variant: "primary" },
});

export default function Button({
  as = "button",
  to,
  href,
  onClick,
  children,
  variant = "primary",
  withArrow = false,
  className = "",
  type = "button",
  ...rest
}) {
  const reduceMotion = useReducedMotion();
  const arrow = withArrow ? (
    <motion.span
      aria-hidden="true"
      className="inline-flex"
      initial={false}
      animate={{ x: 0 }}
      whileHover={reduceMotion ? undefined : { x: 4 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <ArrowRight className="h-4 w-4" />
    </motion.span>
  ) : null;

  const cls = cn(buttonVariants({ variant }), className);

  if (as === "link" && to) {
    return (
      <MotionLink
        to={to}
        onClick={onClick}
        className={cls}
        whileHover={reduceMotion ? undefined : { y: -1 }}
        whileTap={reduceMotion ? undefined : { scale: 0.98 }}
        {...rest}
      >
        {children}
        {arrow}
      </MotionLink>
    );
  }

  if (as === "a" && href) {
    return (
      <MotionA
        href={href}
        onClick={onClick}
        className={cls}
        whileHover={reduceMotion ? undefined : { y: -1 }}
        whileTap={reduceMotion ? undefined : { scale: 0.98 }}
        {...rest}
      >
        {children}
        {arrow}
      </MotionA>
    );
  }

  return (
    <MotionButton
      type={type}
      onClick={onClick}
      className={cls}
      whileHover={reduceMotion ? undefined : { y: -1 }}
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      {...rest}
    >
      {children}
      {arrow}
    </MotionButton>
  );
}

